import { iframeDataBridge } from '@/iframe/iframeDataBridge';

// Pure port of visitPreview.js init()'s classification (public/m.eagle/app/components/sales/visitPreview.js).
// The 3 side effects it also performs (removeDraftAppointment2 cleanup, saveAppointmentSyncError
// logging, ctx.user.settings VisitPreparations rewrite) are NOT here - they stay in legacyNavState.js,
// which calls the legacy modules directly, keeping this module free of any legacy window access.

const STATUS = iframeDataBridge.enumAppointment.status;

// dateHelper.dateOnly(): truncates to midnight, local time.
export function dateOnly(d) {
	const date = d ? new Date(d) : new Date();
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// dateHelper.addDays(): calendar-day arithmetic (via setDate), DST-safe unlike a raw ms offset.
export function addDays(d, nbDays) {
	const date = new Date(d);
	date.setDate(date.getDate() + nbDays);
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// dateHelper.isDateGreater(d1, d2, equal): compares by DATE ONLY, not time-of-day.
export function isDateGreaterDateOnly(d1, d2, equal = false) {
	if (!d1 || !d2) return false;
	const a = dateOnly(d1);
	const b = dateOnly(d2);
	return equal ? a >= b : a > b;
}

// dateHelper.isDateTimeLess(d1, d2, equal): compares truncated to the minute.
export function isDateTimeLessMinute(d1, d2, equal = false) {
	if (!d1 || !d2) return false;
	const x = new Date(d1);
	const y = new Date(d2);
	const a = new Date(x.getFullYear(), x.getMonth(), x.getDate(), x.getHours(), x.getMinutes());
	const b = new Date(y.getFullYear(), y.getMonth(), y.getDate(), y.getHours(), y.getMinutes());
	return equal ? a <= b : a < b;
}

// visitPreview.js init(), lines 168-195: late (minus anything already a draft) + drafts + any
// VisitPreparations entry not already present, refreshing its dates from allAppointments if it
// still exists there (in case the source appointment was edited on another device).
export function buildVisitList({ lateAppointments = [], draftAppointments = [], inPrepaItems = [], allAppointments = [] }) {
	const draftUids = new Set(draftAppointments.map((a) => a.uid));
	let list = lateAppointments.filter((a) => !draftUids.has(a.uid));
	list = list.concat(draftAppointments);

	inPrepaItems.forEach((item) => {
		if (!item?.appointment) return;
		if (!list.some((a) => a.uid === item.appointment.uid)) list.push(item.appointment);
		const source = allAppointments.find((a) => a.uid === item.appointment.uid);
		if (source) {
			item.appointment.startDate = source.startDate;
			item.appointment.endDate = source.endDate;
			item.appointment.deliveryLocationId = source.deliveryLocationId;
		}
	});

	return list;
}

// visitPreview.js init(), lines 197-243. `lateLimit` is dateOnly(addDays(now, -7)).
export function classifyAppointment({ appointment, allAppointments = [], deliveryLocations = [], lateLimit, now = new Date() }) {
	const source = allAppointments.find((a) => a.uid === appointment.uid);

	if (!source && appointment.status !== STATUS.INPROGRESS) return { bucket: 'obsolete' };
	if (source && (source.status === STATUS.CANCELLED || source.status === STATUS.VALIDATED)) return { bucket: 'cancelled' };

	const dl = deliveryLocations.find((d) => d.id === appointment.deliveryLocationId);
	if (!dl) {
		if (appointment.deliveryLocationId > 0 && isDateGreaterDateOnly(appointment.startDate, lateLimit)) return { bucket: 'missing-pos' };
		return { bucket: 'dropped' };
	}

	const startDate = new Date(appointment.startDate);
	const endDate = new Date(appointment.endDate);

	if (appointment.status === STATUS.INPROGRESS) return { bucket: 'visit', dl, startDate, endDate };
	if (appointment.status === STATUS.SCHEDULED) return { bucket: 'late', dl, startDate, endDate };
	if (appointment.status === STATUS.CONFIRMED) return { bucket: isDateGreaterDateOnly(startDate, lateLimit) ? 'preparation' : 'late', dl, startDate, endDate };

	return { bucket: 'dropped' };
}

// visitPreview.js isAppointmentReady(), line 344-351.
export function isAppointmentReady(appointment, { now = new Date(), delayAllowedMs = 0 } = {}) {
	const startDate = new Date(appointment.startDate);
	const timeGap = Math.abs(now.getTime() - startDate.getTime());
	return timeGap <= delayAllowedMs || isDateTimeLessMinute(startDate, now, true);
}
